import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/system';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CropFreeIcon from '@mui/icons-material/CropFree';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';

const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 120,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 50%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.05) 75%, transparent 75%, transparent)',
    backgroundSize: '20px 20px',
    opacity: 0.5,
    zIndex: -1
  }
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));

const aspectRatios = [
  // { value: null, label: 'Serbest', icon: <CropFreeIcon fontSize="small" /> }, // Removed Serbest option
  { value: 1, label: '1:1 (Kare)', icon: <AspectRatioIcon fontSize="small" /> },
  { value: 4/3, label: '4:3', icon: <AspectRatioIcon fontSize="small" /> },
  { value: 16/9, label: '16:9', icon: <AspectRatioIcon fontSize="small" /> },
  { value: 3/2, label: '3:2', icon: <AspectRatioIcon fontSize="small" /> },
  { value: 9/16, label: '9:16 (Dikey)', icon: <AspectRatioIcon fontSize="small" /> },
];

const CropToolOptions = ({ settings, onChange, onApply, currentImage }) => {
  const [previewStyle, setPreviewStyle] = useState({});
  
  // Update preview when settings change
  useEffect(() => {
    updatePreview();
  }, [settings]);
  
  const updatePreview = () => {
    const newStyle = {
      aspectRatio: settings.aspect || 'auto',
      width: settings.aspect ? '80%' : '90%',
      height: settings.aspect ? 'auto' : '90%',
      border: '2px solid #1976d2',
      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
      borderRadius: '4px',
      backgroundImage: `url(${currentImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      transition: 'all 0.3s ease'
    };
    
    setPreviewStyle(newStyle);
  };

  const handleAspectChange = (event) => {
    onChange({ ...settings, aspect: event.target.value });
  };

  const handleUnitChange = (event, newUnit) => {
    if (newUnit !== null) {
      onChange({ ...settings, unit: newUnit });
    }
  };
  
  const handleReset = () => {
    onChange({ aspect: null, unit: 'px' });
  };

  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Stack spacing={2.5}>
          <Box>
            <OptionTitle>
              <CropIcon /> Kırpma Seçenekleri
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Görselinizi istediğiniz boyutlarda kırpın
            </Typography>
          </Box>
          
          <PreviewBox>
            <Box sx={previewStyle}>
              {!currentImage && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Kırpma önizlemesi
                </Typography>
              )}
            </Box>
          </PreviewBox>
          
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="aspect-ratio-label">En/Boy Oranı</InputLabel>
            <Select
              labelId="aspect-ratio-label"
              value={settings.aspect}
              label="En/Boy Oranı"
              onChange={handleAspectChange}
            >
              {aspectRatios.map((ratio) => (
                <MenuItem key={ratio.label} value={ratio.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {ratio.icon}
                    <Typography sx={{ ml: 1 }}>{ratio.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Birim
            </Typography>
            <ToggleButtonGroup
              value={settings.unit}
              exclusive
              onChange={handleUnitChange}
              size="small"
              fullWidth
              color="primary"
            >
              <ToggleButton value="px">Piksel</ToggleButton>
              <ToggleButton value="%">Yüzde</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, gap: 2 }}>
            <Button 
              variant="outlined"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              fullWidth
            >
              Sıfırla
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => onApply(true)}
              fullWidth
            >
              Uygula
            </Button>
          </Box>
        </Stack>
      </Box>
    </OptionsPaper>
  );
};

export default CropToolOptions;
